import { cn } from '@multaqa/ui/lib/utils'
import type * as React from 'react'

/**
 * The scroll box a wide report table sits in, with its column header pinned.
 *
 * Every accounting report used a plain `overflow-x-auto` div. That div IS a
 * scroll port — `overflow-x: auto` makes `overflow-y` compute to `auto` as
 * well — but its height grows with the table, so it never scrolls vertically
 * and the PAGE scrolls instead. A `sticky` header inside it therefore has
 * nothing to stick to: the column header left the top of the screen with the
 * first rows, and a general ledger or a trial balance became a grid of bare
 * numbers.
 *
 * Bounding the box is what makes the header work: the box is now the thing
 * that scrolls, so `sticky top-0` holds the header against it.
 *
 * The whole `<thead>` is pinned, not one row of it — the trial balance's
 * header is two rows deep (a span row over a debit/credit row), and pinning
 * the first row alone would leave the column names behind.
 *
 * `bg-background` is not decoration. Without it the head is transparent and
 * the scrolled rows read straight through the pinned text. The inset shadow
 * draws the divider under it: Tailwind's preflight sets
 * `border-collapse: collapse`, and a collapsed border belongs to the table
 * rather than to the pinned head, so a plain `border-b` scrolls away.
 *
 * PRINT: the trial balance and the statement both export a PDF through
 * `window.print()`. A box fixed at 70vh would print one screenful and drop
 * every row below it in silence, so print takes the height cap off again.
 */
export function TableScroll({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="table-scroll"
      className={cn(
        'relative max-h-[70vh] overflow-auto',
        '[&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:bg-background',
        '[&_thead_th]:shadow-[inset_0_-1px_0_var(--color-border)]',
        'print:max-h-none print:overflow-visible',
        className,
      )}
      {...props}
    />
  )
}
